import { createJSONStorage } from 'zustand/middleware';
import type { PersistStorage, StorageValue } from 'zustand/middleware';

/**
 * `createJSONStorage`, resolved on first use and with a memory fallback.
 *
 * Every `persist` store in the app needs this, and each one that wrote
 * `createJSONStorage(() => localStorage)` inline carries the same two defects.
 *
 * ## Defect 1 — resolving at module import
 *
 * `createJSONStorage(() => sessionStorage)` evaluated at import time reads the
 * global as the module loads. Any context without it — a worker, a server
 * render, a test runner, a browser with site data blocked, Safari in Private
 * Browsing historically — gets a store that throws on the first write, and the
 * failure surfaces far from its cause as "Cannot read properties of undefined
 * (reading 'setItem')" from inside zustand.
 *
 * ## Defect 2 — checking the return value proves nothing
 *
 * The subtlety that cost me a wrong fix once already: `createJSONStorage`
 * returns a fully formed object even when its getter yields `undefined` without
 * throwing. It only returns `undefined` if the getter THROWS. So `?? fallback`
 * on its result never fires in the case that actually happens — the object's
 * `setItem` closes over the undefined storage and fails on first write.
 *
 * Probing the storage directly is what answers the question, and a write probe
 * rather than a presence check: a `localStorage` that exists but throws on use
 * (privacy-restricted, or quota-exhausted) is not usable either.
 *
 * Falling back to memory makes the absence survivable. Persistence is an
 * enhancement; losing it should cost the user their saved draft, not the whole
 * screen.
 */

/**
 * The fallback, with the SAME contract as `createJSONStorage`.
 *
 * That contract is easy to get wrong, and I did: `createJSONStorage` PARSES on
 * `getItem` and stringifies on `setItem`, so a fallback returning raw strings
 * hands the caller a different type in the branch that only fires when
 * something is already wrong. A test caught it; nothing else would have, since
 * the fallback never runs in a browser.
 */
export function memoryStorage<T>(): PersistStorage<T> {
  const map = new Map<string, string>();
  return {
    getItem: (name) => {
      const raw = map.get(name);
      return raw ? (JSON.parse(raw) as StorageValue<T>) : null;
    },
    setItem: (name, value) => void map.set(name, JSON.stringify(value)),
    removeItem: (name) => void map.delete(name),
  };
}

type Kind = 'local' | 'session';

function read(kind: Kind): Storage | undefined {
  // `typeof` guards rather than `globalThis.x`: in some runtimes the global is
  // declared but accessing it throws, which the try/catch at the call site
  // covers.
  try {
    // Typed non-nullable by the DOM lib, but absent at runtime in a worker, a
    // server render, or a test environment — hence the cast and the catch.
    const store: Storage | undefined =
      kind === 'local' ? localStorage : sessionStorage;
    return store;
  } catch {
    return undefined;
  }
}

function usable<T>(kind: Kind): PersistStorage<T> | undefined {
  const store = read(kind);
  if (!store) return undefined;
  try {
    const probe = '__persist_probe__';
    store.setItem(probe, '1');
    store.removeItem(probe);
  } catch {
    return undefined;
  }
  return createJSONStorage<T>(() => store);
}

const resolved = new Map<Kind, PersistStorage<unknown>>();

/**
 * A `PersistStorage` for `persist({ storage })` that cannot be `undefined`.
 *
 * Resolution is deferred to the first read or write and then memoised, so the
 * module import order stops mattering.
 */
export function safePersistStorage<T>(kind: Kind): PersistStorage<T> {
  const get = () => {
    let r = resolved.get(kind);
    if (!r) {
      r = usable<unknown>(kind) ?? memoryStorage<unknown>();
      resolved.set(kind, r);
    }
    return r as PersistStorage<T>;
  };
  return {
    getItem: (name) => get().getItem(name),
    setItem: (name, value) => get().setItem(name, value),
    removeItem: (name) => get().removeItem(name),
  };
}

/** Test seam — drops the memoised resolution so a test can change the global. */
export function resetPersistStorageForTests(): void {
  resolved.clear();
}
